import { useQuery } from '@apollo/react-hooks';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import React, { useEffect, useRef, useState } from 'react';
import { loader } from 'graphql.macro';
import { get, isEmpty } from 'lodash';

import {
  createSearchableData,
  DxCodeForDisplay,
  filterCodes,
} from './helpers/diagnosisSearchHelpers';
import ComboBox from '../ComboBox/ComboBox';
import { ReactComponent as DeleteIcon } from '../../assets/svgs/icon-delete-gray.svg';
import { BillingCodesInfoBubble } from '../Clients/BillingCodesInfoBubble';

export enum ChooseDXTestID {
  Label = 'ChooseDXTestID.Label',
  SecondaryLabel = 'ChooseDXTestID.SecondaryLabel',
  PrimaryInput = 'ChooseDXTestID.PrimaryInput',
  AddSecondaryBtn = 'ChooseDXTestID.AddSecondaryBtn',
  SecondaryInput = 'ChooseDXTestID.SecondaryInput',
}

interface Props {
  onPrimaryCodeSelected: (dx: string) => void;
  onSecondaryCodeSelected: (dxs: string[]) => void;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
}

interface SelectedCodeDisplayProps {
  displayText: string;
  onChange: () => void;
  onDelete?: () => void;
}

const query = loader('src/components/Invoicing/documents/GetDxCodesQuery.gql');

const SelectedCodeDisplay = ({ displayText, onChange, onDelete }: SelectedCodeDisplayProps) => {
  return (
    <div className="flex justify-between mb-2">
      <button
        className="text-gray-800 flex-grow h-9 leading-9 border border-gray-200 rounded-md px-3 truncate text-left hover:border-blue-400"
        onClick={onChange}
        name={`Edit code ${displayText}`}
        tabIndex={0}
      >
        {displayText}
      </button>
      {!!onDelete && (
        <button
          className="flex-none text-red-400 mx-3"
          onClick={onDelete}
          name={`Remove code ${displayText}`}
          tabIndex={0}
        >
          <DeleteIcon />
        </button>
      )}
    </div>
  );
};

export const ChooseDXCodes: React.FC<Props> = ({
  onPrimaryCodeSelected,
  onSecondaryCodeSelected,
  memberClient,
}) => {
  const { data, loading } = useQuery(query, {
    client: memberClient,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [foundEntries, setFoundEntries] = useState<DxCodeForDisplay[]>([]);

  const [isSelectingPrimary, setIsSelectingPrimary] = useState(true);
  const [selectedPrimaryCode, setSelectedPrimaryCode] = useState('');
  const [selectedDiagnosisDisplayText, setSelectedDiagnosisDisplayText] = useState('');

  const [addSecondaryIntent, setAddSecondaryIntent] = useState(false);
  const [secondaryDxCodes, setSecondaryDxCodes] = useState<DxCodeForDisplay[]>([]);
  const [changingSecondary, setChangingSecondary] = useState<number | null>(null);

  const initialData = useRef(createSearchableData(data?.getDxCodes || []));

  const handleInputChange = (value: string) => {
    if (value.trim().length === 0) {
      setFoundEntries([]);
      setSearchTerm('');
      return;
    }

    const codesToExclude = [selectedPrimaryCode, ...secondaryDxCodes.map(({ code }) => code)];
    const nextEntries =
      searchTerm && value.length > searchTerm.length
        ? filterCodes(foundEntries, value, codesToExclude)
        : filterCodes(initialData.current, value, codesToExclude);

    setSearchTerm(value);
    setFoundEntries(nextEntries);
  };

  const handlePrimarySelect = ({ code, displayText }) => {
    setIsSelectingPrimary(false);
    setSelectedDiagnosisDisplayText(`${displayText}`);
    setSelectedPrimaryCode(code);
    setFoundEntries([]);
    setSearchTerm('');
    onPrimaryCodeSelected(code);
  };

  const handleSecondarySelect = (option) => {
    let nextSecondaryCodes = [...secondaryDxCodes];

    if (changingSecondary !== null) {
      nextSecondaryCodes[changingSecondary] = option;
    }

    nextSecondaryCodes = nextSecondaryCodes.slice(0, 4);

    setSecondaryDxCodes(nextSecondaryCodes);
    setChangingSecondary(null);
    setFoundEntries([]);
    setSearchTerm('');
    onSecondaryCodeSelected(nextSecondaryCodes.map(({ code }) => code));
  };

  const handleChangePrimary = () => {
    setSearchTerm(selectedDiagnosisDisplayText);
    setSelectedPrimaryCode('');
    setFoundEntries(
      filterCodes(initialData.current, selectedDiagnosisDisplayText, [
        '',
        ...secondaryDxCodes.map(({ code }) => code),
      ]),
    );
    setIsSelectingPrimary(true);
    setChangingSecondary(null);
  };

  const handleChangeSecondary = (index: number | null) => {
    const displayText: string = get(secondaryDxCodes, `${index}.displayText`, '');

    if (displayText && index !== null) {
      const nextSecondaryCodes = [...secondaryDxCodes];

      nextSecondaryCodes.splice(index, 1, { displayText } as DxCodeForDisplay);
      setFoundEntries(
        displayText
          ? filterCodes(initialData.current, displayText, [
              selectedDiagnosisDisplayText,
              ...nextSecondaryCodes.map(({ code }) => code),
            ])
          : [],
      );
      setSecondaryDxCodes(nextSecondaryCodes);
    }

    setSearchTerm(displayText);
    setIsSelectingPrimary(false);
    setChangingSecondary(index);
  };

  const addSecondaryCode = () => {
    const nextSecondaryCodes = [...secondaryDxCodes, {} as DxCodeForDisplay];

    if (!addSecondaryIntent) {
      setAddSecondaryIntent(true);
    }

    setIsSelectingPrimary(false);
    setChangingSecondary(nextSecondaryCodes.length - 1);
    setSecondaryDxCodes(nextSecondaryCodes);
  };

  const removeSecondary = (codeToDelete: string) => {
    const nextSecondaryCodes = secondaryDxCodes.filter(({ code }) => code !== codeToDelete);

    setSecondaryDxCodes(nextSecondaryCodes);
    onSecondaryCodeSelected(nextSecondaryCodes.map(({ code }) => code));
  };

  const shouldDisableAddSecondaryBtn = () => {
    if (!selectedPrimaryCode) {
      return true;
    }

    return secondaryDxCodes.length > 0 && secondaryDxCodes.some((codeObj) => isEmpty(codeObj));
  };

  useEffect(() => {
    if (!data) return;

    initialData.current = createSearchableData(data.getDxCodes);
  });

  const addSecondaryDisabled = shouldDisableAddSecondaryBtn();

  return (
    <>
      <div className="flex items-center">
        <label
          className="text-sm font-black text-gray-800 leading-loose"
          data-testid={ChooseDXTestID.Label}
          data-loading={loading}
        >
          <span className="text-red-500">*</span> Diagnosis code
        </label>
        <BillingCodesInfoBubble />
      </div>
      {isSelectingPrimary ? (
        <div className="mb-2" data-testid={ChooseDXTestID.PrimaryInput}>
          <ComboBox
            startingValue={selectedDiagnosisDisplayText}
            maxResultsCount={5}
            onInputChange={handleInputChange}
            onSelect={handlePrimarySelect}
            selectableOptions={foundEntries}
            placeholder="Search for a primary diagnosis code"
            stackSubText
          />
        </div>
      ) : (
        <SelectedCodeDisplay
          displayText={selectedDiagnosisDisplayText}
          onChange={handleChangePrimary}
        />
      )}
      {addSecondaryIntent && (
        <label
          className="text-sm font-black text-gray-800 leading-loose"
          data-testid={ChooseDXTestID.SecondaryLabel}
        >
          Secondary diagnosis code
        </label>
      )}
      {secondaryDxCodes.length > 0 &&
        secondaryDxCodes.map((secondaryCode, idx) =>
          idx === changingSecondary ? (
            <div
              key="selecting-secondary-code"
              className="flex justify-between mb-2"
              tabIndex={0}
              data-testid={ChooseDXTestID.SecondaryInput}
            >
              <div className="flex-grow">
                <ComboBox
                  startingValue={secondaryCode.displayText || undefined}
                  maxResultsCount={5}
                  onInputChange={handleInputChange}
                  onSelect={handleSecondarySelect}
                  selectableOptions={foundEntries}
                  placeholder="Search for a secondary diagnosis code"
                  stackSubText
                />
              </div>
              <button
                className="flex-none text-red-400 mx-3"
                onClick={() => removeSecondary(secondaryCode.code)}
                name={`Remove code ${secondaryCode.displayText}`}
                tabIndex={0}
              >
                <DeleteIcon />
              </button>
            </div>
          ) : (
            <SelectedCodeDisplay
              key={secondaryCode.code}
              displayText={secondaryCode.displayText || 'Search for a secondary diagnosis code'}
              onDelete={() => removeSecondary(secondaryCode.code)}
              onChange={() => handleChangeSecondary(idx)}
            />
          ),
        )}

      {secondaryDxCodes.length < 4 && (
        <button
          className={`text-${addSecondaryDisabled ? 'gray' : 'blue'}-400 px-3 text-left w-full${
            addSecondaryDisabled ? ' cursor-not-allowed' : ''
          }`}
          disabled={addSecondaryDisabled}
          onClick={addSecondaryCode}
          data-testid={ChooseDXTestID.AddSecondaryBtn}
        >
          <span className="text-xl">+</span> Add a secondary diagnosis code
        </button>
      )}
    </>
  );
};
